import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import { ScreenConfigInformationForm } from '@components/cockpit/panels/operatorStationConfiguration/context/ScreenConfigInformationForm';
import { ScreenContextAssignmentForm } from '@components/cockpit/panels/operatorStationConfiguration/context/ScreenContextAssignmentForm';
import { useOperatorStationConfigurationContext } from '@components/cockpit/panels/operatorStationConfiguration/OperatorStationConfigurationContext';
import ModifiedTag from '@components/common/form/ModifiedField';
import { Form } from '@components/ui/Form';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { selectGlobalScreenConfigurationById } from '@redux/config/config.selectors';
import {
  useCreateGlobalScreenConfigurationMutation,
  useUpdateGlobalScreenConfigurationMutation,
} from '@services/config/globalScreenConfiguration.api';
import { areSelectedFieldsEqual } from '@utils/common.utils';
import { screenConfigurationSchema } from '@validations/screenConfigurationSchema';
import { Formik } from 'formik';
import { isEqual } from 'lodash';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import {
  ContextEnum,
  GlobalScreenConfiguration,
  GlobalScreenConfigurationWithNullableCoordinates,
  ScreenConfiguration,
} from '@/types/config/screenConfiguration.types';

type ScreenConfigurationDialogProps = {
  isNew: boolean;
  open: boolean;
  globalConfigName: string | undefined;
  screenConfigs: ScreenConfiguration[] | undefined;
} & Omit<CockpitDialogProps, 'children'>;

function ScreenConfigurationDialog({
  isNew = false,
  open = false,
  globalConfigName,
  screenConfigs,
  isLoading,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<ScreenConfigurationDialogProps>) {
  const { formatMessage } = useIntl();

  return (
    <CockpitDialog
      open={open}
      isLoading={isLoading}
      confirmLabel={confirmLabel || formatMessage({ id: 'global.save' })}
      cancelLabel={cancelLabel || formatMessage({ id: 'global.cancel' })}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage
          id={`components.operatorStationConfiguration.context.popup.${isNew ? 'descriptionSaveNew' : 'descriptionSave'}`}
          values={{ name: globalConfigName }}
        />
      </Text>
      {screenConfigs?.length === 0 && (
        <Text color="neutral.100" width="100%" whiteSpace="pre-line">
          <FormattedMessage id="components.operatorStationConfiguration.context.popup.descriptionSaveScreenConfig" />
        </Text>
      )}
    </CockpitDialog>
  );
}

export default function GlobalScreenConfigurationFormContainer() {
  const { formatMessage } = useIntl();

  const validationSchema = useValidation(screenConfigurationSchema);
  const { editedItem, setEditedItem } = useOperatorStationConfigurationContext();
  const activeSite = useAppSelector(selectActiveSite);

  const DEFAULT_GLOBAL_SCREEN_CONFIG: Partial<GlobalScreenConfigurationWithNullableCoordinates> = {
    code: uuidv4(),
    name: '',
    site: activeSite?.code ?? 'local',
    screenConfigurations: Object.values(ContextEnum).flatMap((context) => [
      {
        code: '1',
        gridLayoutCode: null,
        context,
      },
      {
        code: '2',
        gridLayoutCode: null,
        context,
      },
    ]),
    createdBy: '',
    creationTime: '',
    modifiedBy: '',
    modificationTime: '',
    position: { longitude: null, latitude: null, type: '2D' },
    mapZoom: 13,
  };

  const globalScreenConfig =
    useAppSelector((state) => selectGlobalScreenConfigurationById(state, editedItem?.id)) ??
    DEFAULT_GLOBAL_SCREEN_CONFIG;

  const isNewGlobalConfig = globalScreenConfig.id === undefined;

  const [open, setOpen] = useState(false);

  const [createGlobalScreenConfig, { isLoading: isLoadingCreate }] = useCreateGlobalScreenConfigurationMutation();
  const [updateGlobalScreenConfig, { isLoading: isLoadingUpdate }] = useUpdateGlobalScreenConfigurationMutation();

  const handleSubmit = (values: Partial<GlobalScreenConfigurationWithNullableCoordinates>) => {
    if (isNewGlobalConfig) {
      createGlobalScreenConfig(values as GlobalScreenConfiguration)
        .unwrap()
        .then((globalScreenConfig) => {
          setOpen(false);
          createToast(
            formatMessage(
              { id: 'components.operatorStationConfiguration.context.message.add' },
              { name: globalScreenConfig.name },
            ),
            ToastStatusEnum.SUCCESS,
          );
          setEditedItem(null);
        });
    } else {
      updateGlobalScreenConfig({ id: values.id!, globalScreenConfiguration: values as GlobalScreenConfiguration })
        .unwrap()
        .then((globalScreenConfig) => {
          setOpen(false);
          createToast(
            formatMessage(
              { id: 'components.operatorStationConfiguration.context.message.update' },
              { name: globalScreenConfig.name },
            ),
            ToastStatusEnum.SUCCESS,
          );
          setEditedItem(null);
        });
    }
  };

  return (
    <Formik
      initialValues={globalScreenConfig}
      validationSchema={validationSchema}
      validateOnBlur
      onSubmit={handleSubmit}
    >
      {({ initialValues, values, isValid, submitForm }) => (
        <Form style={{ width: '100%', height: '100%' }}>
          <CockpitEditorLayout
            dialog={
              <ScreenConfigurationDialog
                isNew={isNewGlobalConfig}
                open={open}
                globalConfigName={values.name}
                screenConfigs={values.screenConfigurations}
                isLoading={isLoadingCreate || isLoadingUpdate}
                onConfirm={submitForm}
                onCancel={() => setOpen(false)}
              />
            }
            onBack={() => setEditedItem(null)}
            breadcrumbs={
              <Breadcrumb
                items={[
                  {
                    label: formatMessage({ id: 'screenConfiguration.title' }),
                    action: () => setEditedItem(null),
                  },
                  {
                    label: isNewGlobalConfig
                      ? formatMessage({
                          id: 'components.operatorStationConfiguration.context.general.newGlobalScreenConfig',
                        })
                      : values.name,
                  },
                ]}
              />
            }
            onSave={() => setOpen(true)}
            isSaving={isLoadingCreate || isLoadingUpdate}
            isSaveDisabled={
              !isValid || areSelectedFieldsEqual(values, initialValues, ['name', 'position', 'screenConfigurations'])
            }
          >
            <Tabs.Root orientation="vertical" defaultValue="general" lazyMount variant="vertical" userSelect="none">
              <Tabs.List>
                <Tabs.Trigger value="general">
                  <HStack width="100%">
                    <FormattedMessage id="components.operatorStationConfiguration.context.general.title" />
                    {!areSelectedFieldsEqual(values, initialValues, ['name', 'position']) && <ModifiedTag />}
                  </HStack>
                </Tabs.Trigger>
                <Tabs.Trigger value="screens">
                  <HStack width="100%">
                    <FormattedMessage id="components.operatorStationConfiguration.context.assignScreensToContexts.title" />
                    {!isEqual(values.screenConfigurations, initialValues.screenConfigurations) && <ModifiedTag />}
                  </HStack>
                </Tabs.Trigger>
              </Tabs.List>
              <Tabs.ContentGroup backgroundColor="neutral.700">
                <Tabs.Content value="general">
                  <ScreenConfigInformationForm />
                </Tabs.Content>
                <Tabs.Content value="screens">
                  <ScreenContextAssignmentForm />
                </Tabs.Content>
              </Tabs.ContentGroup>
            </Tabs.Root>
          </CockpitEditorLayout>
        </Form>
      )}
    </Formik>
  );
}
